import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, articles } from '@/mocks/sports';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  const relatedArticles = articles
    .filter(a => a.slug !== slug && a.categorySlug === article?.categorySlug)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <div className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-5 rounded-full bg-background-200">
                <i className="ri-article-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章不存在</h1>
              <p className="text-sm text-foreground-500 mb-6">该文章可能已被删除或链接无效</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '开云爱体育',
      logo: {
        '@type': 'ImageObject',
        url: 'https://readdy.ai/api/search-image?query=Kaiyun%20sports%20brand%20logo%20in%20Chinese%20simplified%20elegant%20minimalist%20design%20red%20and%20gold%20on%20transparent%20background&width=200&height=60&seq=logo-kaiyun&orientation=landscape',
      },
    },
  };

  return (
    <>
      <Navbar />
      <main>
        <article className="pt-20 bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="flex-1 max-w-3xl">
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <Link
                        to={`/category/${article.categorySlug}`}
                        className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full cursor-pointer"
                      >
                        {article.category}
                      </Link>
                      {article.tags.map(tag => (
                        <span key={tag} className="px-2.5 py-1 text-xs text-foreground-500 bg-background-100 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4">
                      {article.title}
                    </h1>

                    <div className="flex items-center gap-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i> {article.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i> {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i> {article.readTime}
                      </span>
                    </div>
                  </div>

                  <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden mb-8">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div
                    className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  <div className="mt-10 pt-8 border-t border-background-200">
                    <div className="flex items-center justify-between">
                      <Link
                        to={`/category/${article.categorySlug}`}
                        className="flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-line"></i> 返回{article.category}列表
                      </Link>
                      <Link
                        to="/"
                        className="flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-home-line"></i> 回到首页
                      </Link>
                    </div>
                  </div>
                </div>

                <aside className="lg:w-80 flex-shrink-0">
                  <div className="sticky top-24">
                    <div className="bg-background-100 rounded-2xl p-5 border border-background-200/70 mb-6">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                        <i className="ri-article-line text-accent-500"></i> 相关推荐
                      </h3>
                      <div className="space-y-4">
                        {relatedArticles.map(rel => (
                          <Link
                            key={rel.slug}
                            to={`/article/${rel.slug}`}
                            className="flex gap-3 group cursor-pointer"
                          >
                            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                              <img src={rel.image} alt={rel.title} title={rel.title} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-xs font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                                {rel.title}
                              </h4>
                              <p className="text-xs text-foreground-400 mt-1">{rel.date}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>

                    <div className="bg-accent-100 rounded-2xl p-5 border border-accent-200/50">
                      <h3 className="font-heading text-sm font-semibold text-accent-800 mb-3">开启体育之旅</h3>
                      <p className="text-xs text-accent-700 mb-4 leading-relaxed">
                        加入开云爱体育，获取更多独家赛事分析和专业报道
                      </p>
                      <Link
                        to="/about"
                        className="block w-full text-center px-4 py-2.5 bg-accent-500 text-foreground-950 text-sm font-semibold rounded-full hover:bg-accent-400 transition-colors cursor-pointer"
                      >
                        立即注册
                      </Link>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}